/**
 * Generic utility for managing settings in localStorage.
 * Use createScopedStorage(userId) for user-specific keys so data doesn't leak between users.
 */

export interface StorageSettings {
  [key: string]: any;
}

export interface ScopedStorage {
  getSetting: <T>(key: string, defaultValue: T) => T;
  setSetting: <T>(key: string, value: T) => void;
  removeSetting: (key: string) => void;
  clearSettingsWithPrefix: (prefix: string) => void;
}

/** Prefix for user-scoped keys. When userId is null (e.g. before auth), uses 'anon' to avoid cross-user leak. */
function getKeyPrefix(userId: string | null | undefined): string {
  return userId ? `lyw_${userId}_` : 'lyw_anon_';
}

/** Returns storage helpers that scope keys by userId. */
export function createScopedStorage(userId: string | null | undefined): ScopedStorage {
  const prefix = getKeyPrefix(userId);
  return {
    getSetting: (key, defaultValue) => getSetting(prefix + key, defaultValue),
    setSetting: (key, value) => setSetting(prefix + key, value),
    removeSetting: (key) => removeSetting(prefix + key),
    clearSettingsWithPrefix: (p) => clearSettingsWithPrefix(prefix + p),
  };
}

/**
 * Get a setting from localStorage
 * @param key The key to retrieve
 * @param defaultValue The default value if the key doesn't exist
 * @returns The stored value or the default value
 */
export function getSetting<T>(key: string, defaultValue: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) {
      return defaultValue;
    }
    return JSON.parse(stored) as T;
  } catch (error) {
    console.warn(`Failed to get setting "${key}" from localStorage:`, error);
    return defaultValue;
  }
}

/**
 * Set a setting in localStorage
 * @param key The key to store
 * @param value The value to store
 */
export function setSetting<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to set setting "${key}" in localStorage:`, error);
  }
}

/**
 * Remove a setting from localStorage
 * @param key The key to remove
 */
export function removeSetting(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Failed to remove setting "${key}" from localStorage:`, error);
  }
}

/**
 * Get multiple settings at once
 * @param keys Object with keys and their default values
 * @returns Object with the same keys and their stored or default values
 */
export function getSettings<T extends StorageSettings>(keys: T): T {
  const result = {} as T;
  for (const [key, defaultValue] of Object.entries(keys)) {
    result[key as keyof T] = getSetting(key, defaultValue);
  }
  return result;
}

/**
 * Set multiple settings at once
 * @param settings Object with keys and values to store
 */
export function setSettings(settings: StorageSettings): void {
  for (const [key, value] of Object.entries(settings)) {
    setSetting(key, value);
  }
}

/**
 * Clear all settings with a specific prefix
 * @param prefix The prefix to match
 */
export function clearSettingsWithPrefix(prefix: string): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error(`Failed to clear settings with prefix "${prefix}":`, error);
  }
}

